'use client'

import { useState, useEffect } from 'react'
import { DashboardLayout } from '@/components/dashboard-layout'
import { Badge } from '@/components/badge'
import { AlertCircle, RefreshCw } from 'lucide-react'
import { LogEvent } from '@/lib/types'
import { format } from 'date-fns'

function LogBadge({ severity }: { severity?: string }) {
  const variants: Record<string, 'default' | 'warning' | 'error'> = {
    low: 'default',
    medium: 'default',
    high: 'warning',
    critical: 'error',
  }
  return <Badge variant={variants[severity || 'low'] || 'default'}>{severity?.toUpperCase() || 'LOW'}</Badge>
}

export default function LogsPage() {
  const [logs, setLogs] = useState<LogEvent[]>([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)

  const fetchLogs = async () => {
    try {
      const response = await fetch('/api/logs')
      if (!response.ok) {
        throw new Error('Failed to fetch logs')
      }
      const data = await response.json()
      setLogs(data)
      setError(null)
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to load logs')
    } finally {
      setLoading(false)
    }
  }

  useEffect(() => {
    fetchLogs()
    const interval = setInterval(fetchLogs, 2000) // Poll every 2 seconds
    return () => clearInterval(interval)
  }, [])

  return (
    <DashboardLayout
      title="SOC Attack Feed"
      subtitle="Real-time honeypot attack logs and security events."
      actions={
        <button
          onClick={fetchLogs}
          disabled={loading}
          className="px-4 py-2 bg-primary text-primary-foreground rounded-lg font-medium hover:opacity-90 transition-opacity flex items-center gap-2 disabled:opacity-50"
        >
          <RefreshCw size={18} className={loading ? 'animate-spin' : ''} />
          Refresh
        </button>
      }
    >
      <div className="space-y-4">
        {error && (
          <div className="bg-red-500/10 border border-red-500/20 rounded-lg p-4 flex items-center gap-3">
            <AlertCircle className="text-red-500" size={20} />
            <p className="text-sm text-red-400">{error}</p>
          </div>
        )}

        {loading && logs.length === 0 ? (
          <div className="text-center py-12">
            <RefreshCw className="animate-spin mx-auto mb-4 text-muted-foreground" size={32} />
            <p className="text-muted-foreground">Loading logs...</p>
          </div>
        ) : logs.length === 0 ? (
          <div className="text-center py-12">
            <p className="text-muted-foreground">No logs found</p>
          </div>
        ) : (
          <div className="bg-card border border-border rounded-lg overflow-hidden">
            <div className="divide-y divide-border">
              {logs.map((log, idx) => (
                <div key={log.id || idx} className="p-4 hover:bg-secondary/20 transition-colors">
                  <div className="flex items-start justify-between gap-4">
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-3 mb-2">
                        <LogBadge severity={log.severity} />
                        {log.honeypot_name && (
                          <Badge variant="outline" className="text-xs">
                            {log.honeypot_name}
                          </Badge>
                        )}
                        <span className="text-sm font-mono text-foreground">{log.source_ip}</span>
                        <span className="text-xs text-muted-foreground">•</span>
                        <span className="text-sm text-muted-foreground">{log.protocol}</span>
                      </div>
                      <p className="text-xs text-muted-foreground mb-2">
                        {log.ts ? format(new Date(log.ts), 'PPpp') : 'Unknown time'}
                      </p>
                      {log.mitre_tags && log.mitre_tags.length > 0 && (
                        <div className="flex flex-wrap gap-1 mb-2">
                          {log.mitre_tags.map((tag) => (
                            <Badge key={tag} variant="outline" className="text-xs bg-yellow-500/10 text-yellow-400 border-yellow-500/20">
                              {tag}
                            </Badge>
                          ))}
                        </div>
                      )}
                      <div className="mt-2 p-2 bg-secondary/50 rounded text-xs font-mono text-muted-foreground overflow-x-auto">
                        <pre>{JSON.stringify(log.payload, null, 2)}</pre>
                      </div>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}
      </div>
    </DashboardLayout>
  )
}
